import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Save, Loader2, FileType2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import RichTextEditor from "../../components/admin/RichTextEditor";
import { Switch } from "../../components/ui/switch";

const PAGES = [
    { slug: "hakkimizda", title: "Hakkımızda" },
    { slug: "markalarimiz", title: "Markalarımız" },
    { slug: "kvkk", title: "KVKK / Gizlilik" },
];

const LANGS = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
];

const emptyTrans = () => ({ tr: "", en: "", de: "" });

export default function AdminPages() {
    const [active, setActive] = useState("hakkimizda");
    const [data, setData] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchPage = async (slug) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/pages/${slug}`).catch(() => ({ data: null }));
            if (data) {
                setData((p) => ({ ...p, [slug]: data }));
            } else {
                setData((p) => ({
                    ...p,
                    [slug]: {
                        slug,
                        title: emptyTrans(),
                        content: emptyTrans(),
                        seo: { meta_title: "", meta_desc: "" },
                        is_active: true,
                    },
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!data[active]) fetchPage(active);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const cur = data[active];

    const update = (path, value) => {
        setData((p) => {
            const next = JSON.parse(JSON.stringify(p));
            const obj = next[active];
            const segs = path.split(".");
            let o = obj;
            for (let i = 0; i < segs.length - 1; i++) o = o[segs[i]] = o[segs[i]] || {};
            o[segs[segs.length - 1]] = value;
            return next;
        });
    };

    const save = async () => {
        if (!cur) return;
        setSaving(true);
        try {
            const { data: saved } = await api.put(`/admin/pages/${active}`, {
                title: cur.title,
                content: cur.content,
                seo: cur.seo || {},
                is_active: !!cur.is_active,
            });
            setData((p) => ({ ...p, [active]: saved }));
            toast.success("Sayfa kaydedildi");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-pages">
            <div>
                <h1 className="text-2xl font-bold text-dark-navy">Sayfa Yönetimi</h1>
                <p className="text-sm text-deep-blue/70">Hakkımızda, Markalarımız ve KVKK içeriklerini TR/EN/DE olarak yönetin.</p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                {PAGES.map((p) => (
                    <button
                        key={p.slug}
                        onClick={() => setActive(p.slug)}
                        data-testid={`page-tab-${p.slug}`}
                        className={`text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 ${active === p.slug ? "bg-dark-navy text-white" : "bg-white border border-slate-200 text-deep-blue hover:bg-light-bg"}`}
                    >
                        <FileType2 className="w-3.5 h-3.5" /> {p.title}
                    </button>
                ))}
            </div>

            {loading || !cur ? (
                <div className="text-sm text-slate-500">Yükleniyor...</div>
            ) : (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-dark-navy">Sayfa aktif</span>
                            <Switch checked={!!cur.is_active} onCheckedChange={(v) => update("is_active", v)} data-testid="page-active-toggle" />
                        </div>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                            data-testid="page-save-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Kaydet
                        </button>
                    </div>

                    <Tabs defaultValue="tr">
                        <TabsList className="bg-light-bg">
                            {LANGS.map((l) => (
                                <TabsTrigger
                                    key={l.code}
                                    value={l.code}
                                    data-testid={`page-tab-lang-${l.code}`}
                                    className="data-[state=active]:bg-dark-navy data-[state=active]:text-white"
                                >
                                    {l.name}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {LANGS.map((l) => (
                            <TabsContent key={l.code} value={l.code} className="pt-4 space-y-4">
                                <Section title={`Başlık (${l.code.toUpperCase()})`}>
                                    <input
                                        value={cur.title?.[l.code] || ""}
                                        onChange={(e) => update(`title.${l.code}`, e.target.value)}
                                        className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                        data-testid={`page-title-${l.code}`}
                                    />
                                </Section>
                                <Section title={`İçerik (${l.code.toUpperCase()})`}>
                                    <RichTextEditor
                                        value={cur.content?.[l.code] || ""}
                                        onChange={(html) => update(`content.${l.code}`, html)}
                                        testId={`page-content-${l.code}`}
                                    />
                                </Section>
                            </TabsContent>
                        ))}
                    </Tabs>

                    <Section title="SEO">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Meta Title">
                                <input
                                    value={cur.seo?.meta_title || ""}
                                    onChange={(e) => update("seo.meta_title", e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                    data-testid="page-meta-title"
                                />
                            </Field>
                            <Field label="Meta Description">
                                <input
                                    value={cur.seo?.meta_desc || ""}
                                    onChange={(e) => update("seo.meta_desc", e.target.value)}
                                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                    data-testid="page-meta-desc"
                                />
                            </Field>
                        </div>
                    </Section>
                </div>
            )}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">{title}</h3>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
